import { createClient } from '@supabase/supabase-js';
import { logger } from './logger';
import { SUPABASE_URL } from './supabaseConstants';
import { toUserMessage, USER_ERROR_FALLBACK_ALT } from './toUserMessage';

const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!SUPABASE_URL) {
    logger.error('❌ REACT_APP_SUPABASE_URL не найден!');
    logger.error('Убедитесь, что в .env задан URL проекта (Settings → API → Project URL) и dev-сервер перезапущен.');
    throw new Error('REACT_APP_SUPABASE_URL не настроен. Проверьте .env файл и перезапустите сервер.');
}

// Проверяем наличие ключа
if (!SUPABASE_ANON_KEY) {
    logger.error('❌ REACT_APP_SUPABASE_ANON_KEY не найден!');
    logger.error('Убедитесь, что:');
    logger.error('1. Файл .env существует в корне проекта');
    logger.error('2. В .env есть строка: REACT_APP_SUPABASE_ANON_KEY=ваш_ключ');
    logger.error('3. Dev-сервер перезапущен после изменения .env');

    if (process.env.NODE_ENV === 'production') {
        throw new Error('REACT_APP_SUPABASE_ANON_KEY не настроен. Проверьте .env файл и перезапустите сервер.');
    }

    logger.warn('Продолжаем запуск в development без Supabase ключа. Авторизация и запросы к Supabase будут недоступны.');
}

// Создаем клиент Supabase
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
    },
});

// Подавляем ошибку "No checkout popup config found" (не критична, связана с конфигурацией Supabase)
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason?.message?.includes('checkout popup config')) {
            event.preventDefault();
            logger.warn('Supabase checkout popup config warning (можно игнорировать)');
        }
    });
}

// Ключи для localStorage
const STORAGE_KEYS = {
    ACCESS_TOKEN: 'dronekz_access_token',
    REFRESH_TOKEN: 'dronekz_refresh_token',
    USER_DATA: 'dronekz_user_data',
};

/**
 * Локализация сообщений об ошибках Supabase Auth.
 * Делегирует в единый Error Mapper (ADR-004).
 * @param {string} errorMessage - Оригинальное сообщение об ошибке
 * @param {string} errorCode - Код ошибки (если есть)
 * @returns {string} - Пользовательское сообщение
 */
export const localizeAuthError = (errorMessage, errorCode) => {
    return toUserMessage(
        { message: errorMessage, code: errorCode },
        { fallback: USER_ERROR_FALLBACK_ALT }
    );
};

/**
 * Авторизация пользователя
 * @param {string} email - Email пользователя
 * @param {string} password - Пароль пользователя
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const login = async (email, password) => {
    try {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            return {
                success: false,
                error: localizeAuthError(error.message, error.code),
            };
        }

        if (data?.user && data?.session) {
            // Сохраняем токены и данные пользователя
            const userData = {
                id: data.user.id,
                email: data.user.email,
                phone: data.user.phone,
                created_at: data.user.created_at,
                user_metadata: data.user.user_metadata,
            };

            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.session.access_token);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.session.refresh_token);
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));

            return {
                success: true,
                data: {
                    user: userData,
                    session: {
                        access_token: data.session.access_token,
                        refresh_token: data.session.refresh_token,
                        expires_at: data.session.expires_at,
                    },
                },
            };
        }

        return {
            success: false,
            error: 'Не удалось получить данные пользователя',
        };
    } catch (error) {
        return {
            success: false,
            error: localizeAuthError(error.message, error.code),
        };
    }
};

/**
 * Регистрация нового пользователя
 * @param {string} email - Email пользователя
 * @param {string} password - Пароль пользователя
 * @param {object} metadata - Дополнительные данные пользователя
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const register = async (email, password, metadata = {}) => {
    try {
        // URL для редиректа после подтверждения email
        const redirectTo = `${window.location.origin}/login`;

        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: metadata,
                emailRedirectTo: redirectTo,
            },
        });

        if (error) {
            return {
                success: false,
                error: localizeAuthError(error.message, error.code),
            };
        }

        if (data?.user) {
            const userData = {
                id: data.user.id,
                email: data.user.email,
                phone: data.user.phone,
                created_at: data.user.created_at,
                user_metadata: data.user.user_metadata,
            };

            // Если есть сессия, сохраняем токены
            if (data.session) {
                localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.session.access_token);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.session.refresh_token);
                localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
            }

            return {
                success: true,
                data: {
                    user: userData,
                    session: data.session,
                },
            };
        }

        return {
            success: false,
            error: 'Не удалось создать пользователя',
        };
    } catch (error) {
        return {
            success: false,
            error: localizeAuthError(error.message, error.code),
        };
    }
};

/**
 * Логирование действия пользователя
 * @param {string} actionType - Тип действия
 * @param {string} description - Описание действия
 * @param {object} metadata - Дополнительные данные
 * @param {string|null} userId - ID пользователя (опционально, для неудачных попыток входа)
 */
export const logUserActivity = async (actionType, description, metadata = {}, userId = null) => {
    try {
        let finalUserId = userId;

        // Для большинства действий получаем user из сессии
        if (!finalUserId && actionType !== 'login_failed') {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            finalUserId = user.id;
        }

        // Для неудачных попыток входа пытаемся найти пользователя по email
        if (actionType === 'login_failed' && !finalUserId && metadata.email) {
            try {
                const { data: profiles } = await supabase
                    .from('profiles')
                    .select('id')
                    .eq('email', metadata.email)
                    .limit(1)
                    .maybeSingle();

                if (profiles) {
                    finalUserId = profiles.id;
                }
            } catch (err) {
                // Если не удалось найти пользователя, оставляем user_id = null
                logger.warn('Не удалось найти пользователя по email для логирования:', err);
            }
        }

        // Получаем IP и User-Agent (если доступны)
        const ipAddress = metadata.ip_address || null;
        const userAgent = metadata.user_agent || navigator.userAgent || null;

        const { error } = await supabase
            .from('user_activity_logs')
            .insert({
                user_id: finalUserId, // может быть null для неудачных попыток входа
                action_type: actionType,
                action_description: description,
                ip_address: ipAddress,
                user_agent: userAgent,
                metadata: metadata
            });

        if (error) {
            logger.error('Ошибка логирования действия:', error);
        }
    } catch (error) {
        logger.error('Ошибка при логировании действия:', error);
        // Не прерываем выполнение, если логирование не удалось
    }
};

/**
 * Выход из системы
 */
export const logout = async () => {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        // Логируем выход перед выходом
        if (user) {
            await logUserActivity(
                'logout',
                'Выход из аккаунта',
                {}
            );
        }

        await supabase.auth.signOut();
        clearAuthData();
        clearRolesCache(); // Очищаем кэш ролей при выходе
    } catch (error) {
        logger.error('Ошибка при выходе:', error);
        clearAuthData();
        clearRolesCache(); // Очищаем кэш ролей при выходе
    }
};

/**
 * Очистка данных авторизации из localStorage
 */
export const clearAuthData = () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

/**
 * Получение данных пользователя из localStorage
 * @returns {object|null}
 */
export const getUserData = () => {
    try {
        const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        return userData ? JSON.parse(userData) : null;
    } catch (error) {
        logger.error('Ошибка при получении данных пользователя:', error);
        return null;
    }
};

/**
 * Получение access token из localStorage
 * @returns {string|null}
 */
export const getAccessToken = () => {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Получение refresh token из localStorage
 * @returns {string|null}
 */
export const getRefreshToken = () => {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Проверка, авторизован ли пользователь
 * @returns {boolean}
 */
export const isAuthenticated = () => {
    const token = getAccessToken();
    const userData = getUserData();
    return !!(token && userData);
};

/**
 * Обновление токена
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const refreshToken = async () => {
    try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
            return { success: false, error: 'Refresh token не найден' };
        }

        const { data, error } = await supabase.auth.refreshSession({
            refresh_token: refreshToken,
        });

        if (error) {
            clearAuthData();
            return { success: false, error: localizeAuthError(error.message, error.code) };
        }

        if (data?.session) {
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.session.access_token);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.session.refresh_token);
            return { success: true };
        }

        return { success: false, error: 'Не удалось обновить токен' };
    } catch (error) {
        clearAuthData();
        return { success: false, error: localizeAuthError(error.message, error.code) };
    }
};

// Кэш для ролей пользователей
const rolesCache = new Map();
const CACHE_TTL = 60 * 1000; // 1 минута — роли обновляются быстрее после смены в Admin

/**
 * Получение ролей пользователя из базы данных с кэшированием
 * @param {string} userId - ID пользователя
 * @returns {Promise<string[]>} - Массив ролей пользователя
 */
export const getUserRoles = async (userId) => {
    try {
        if (!userId) return [];

        // Проверяем кэш
        const cached = rolesCache.get(userId);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            return cached.roles;
        }

        const { data, error } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId);

        if (error) {
            logger.error('Ошибка получения ролей пользователя:', error);
            return [];
        }

        const roles = data?.map(item => item.role) || [];

        // Сохраняем в кэш
        rolesCache.set(userId, {
            roles,
            timestamp: Date.now()
        });

        return roles;
    } catch (error) {
        logger.error('Ошибка при получении ролей:', error);
        return [];
    }
};

/**
 * Очистка кэша ролей для пользователя
 * @param {string} userId - ID пользователя (опционально, если не указан - очищает весь кэш)
 */
export const clearRolesCache = (userId = null) => {
    if (userId) {
        rolesCache.delete(userId);
    } else {
        rolesCache.clear();
    }
};

/**
 * Проверка, имеет ли пользователь одну из указанных ролей
 * @param {string[]} allowedRoles - Массив разрешенных ролей
 * @returns {Promise<boolean>} - true если пользователь имеет одну из ролей
 */
export const hasRole = async (allowedRoles = []) => {
    try {
        if (!isAuthenticated()) return false;

        const user = getUserData();
        if (!user?.id) return false;

        const userRoles = await getUserRoles(user.id);
        return allowedRoles.some(role => userRoles.includes(role));
    } catch (error) {
        logger.error('Ошибка проверки роли:', error);
        return false;
    }
};

/**
 * Проверка, является ли пользователь администратором или модератором
 * @returns {Promise<boolean>}
 */
export const isAdminOrModerator = async () => {
    return hasRole(['admin', 'moderator']);
};
